import { useValidators } from "@/shared/utils/validate";
import { ErrorDialogContext, errorHandledAsync, errorHandledSync, } from "@/shared/context/error-context";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { LoadingModalContext } from "@/shared/components/loading/loading-context";
import { useResetPassStates } from "@/features/user/hooks/use-reset-pass-states";
import { ResetUserPassService } from "@/features/user/services/reset-pass-service";
import { MoneyLinkException } from "@/shared/utils/custom-exception";
import { ErrorType } from "@/shared/config/error-type";
import { EndpointPathNames } from "@/shared/config/endpoint-path";

// ログイン画面で利用している「パスワードを忘れた方、お困りの方はこちら」
export const useResetPassSet = () => {
  const resetUserPassService = new ResetUserPassService();
  const navigate = useNavigate();
  const {
    password,
    setPassword,
    passwordConfirm,
    setPasswordConfirm,
    passwordError,
    setPasswordError,
    isActiveUpdateButton,
    setIsActiveUpdateButton,
    isShowErrorDialog, // エラーダイアログ表示フラグ情報
    setIsShowErrorDialog,
  } = useResetPassStates();

  // メールアドレスバリデーション
  const { passwordValidator } = useValidators();
  // グローバルエラーハンドリング
  const { setError } = useContext(ErrorDialogContext);
  // ローディング用コンテキスト
  const loadingCtx = useContext(LoadingModalContext);

  // パスワード更新ボタン活性化チェック
  const changeUpdateButtonState = () => {
    setIsActiveUpdateButton(password !== "" && passwordConfirm !== "");
  };

  // パスワードの変更
  const onChangePassword = (value: string) => { setPassword(value);};
  
  // パスワード（確認用）の変更
  const onChangePasswordConfirm = (newValue: string) => { setPasswordConfirm(newValue);};

  // メール送信ボタン押下イベント
  const onClickUpdate = async (token: string, password: string, passwordConfirm: string) => {
    loadingCtx.setIsLoading(true);
    try {
      // 入力チェック
      let passwordErrorMessage = passwordValidator(password);
      passwordErrorMessage = passwordValidator(passwordConfirm);

      if (passwordErrorMessage) {
        // バリデーションエラーの場合、処理終了
        setPasswordError(passwordErrorMessage);
        return;
      }
      if (password !== passwordConfirm) {
        setPasswordError("パスワードと確認用パスワードが一致しません。");
        return;
      }
      // パスワード更新
      await resetUserPassService.resetUserPass({
        token: token,
        owner_pw: password,
      });

      // ユーザー録画完了面へ遷移
      navigateResetPassCompleted();
    } catch (error) {
      if (error instanceof MoneyLinkException) {
        if (error.error.props.reason === ErrorType.ACCOUNTS_CLIENT_PARAMETER_ERROR) {
          // 強制的にリロードさせる
          window.location.reload();
        } else {
          throw MoneyLinkException.wrapCallback(error, () => { /**intentionally empty */ });
        }
      } else {
        throw MoneyLinkException.wrapCallback(error, () => { /**intentionally empty */ });
      }
    } finally {
      loadingCtx.setIsLoading(false);
    }
  };

  // 入力された値とエラーメッセージをリセット
  const resetForm = () => {
    setPassword("");
    setPasswordError("");
  };

  // パスワードリセット完了画面へ遷移
  const navigateResetPassCompleted = () => {
    // ブラウザバックで戻れないよう履歴を置換する
    navigate(EndpointPathNames.RESET_PASS_COMPLETED, { replace: true });
  };

  // エラーダイアログを閉じる
  const closeErrorDialog = () => {
    setIsShowErrorDialog({
      isError: false,
      errorMessage: "",
    });
  };

  return {
    password,
    passwordConfirm,
    passwordError,
    isActiveUpdateButton,
    isShowErrorDialog,
    onChangePassword: errorHandledSync(onChangePassword, setError),
    onChangePasswordConfirm: errorHandledSync(onChangePasswordConfirm, setError),
    onClickUpdate: errorHandledAsync(onClickUpdate, setError),
    changeUpdateButtonState: errorHandledSync(changeUpdateButtonState, setError),
    resetForm: errorHandledSync(resetForm, setError),
    closeErrorDialog: errorHandledSync(closeErrorDialog, setError),
  };
};
